<script setup lang="ts">
import { computed } from 'vue'
import clsx from 'clsx'

import BetaEditorPageChip from '@/components/beta-editor/BetaEditorPageChip.vue'
import BetaEditorViewportToggle from '@/components/beta-editor/BetaEditorViewportToggle.vue'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useEditorPageStore } from '@/stores/editor/page'

const betaStore = useEditorBetaStore()
const editorPageStore = useEditorPageStore()

const emit = defineEmits<{
    back: []
}>()

const loadingGetPage = computed(() => editorPageStore.loadingGetPage)
const page = computed(() => editorPageStore.activePage)

const onClickPublish = () => {
    editorPageStore.publish({
        sanitizeBetaPayload: true,
        redirectTo: '/dashboard?beta',
    })
}
</script>

<template>
    <div class="page-beta-editor-header page-beta-editor-header--with-panel-navigation">
        <div class="page-beta-editor-header__left">
            <button type="button" class="button-back" @click="emit('back')">
                <span class="sr-only">Voltar</span>
                <svg class="h-4 w-4" viewBox="0 0 16 16" fill="none">
                    <path d="M4.63552 3.20005L1.59961 6.27704M1.59961 6.27704L4.63552 9.35404M1.59961 6.27704L11.1381 6.27704C12.9394 6.27704 14.3996 7.73727 14.3996 9.53855C14.3996 11.3398 12.9394 12.8 11.1381 12.8L7.99961 12.8" stroke="currentColor" stroke-opacity="0.5" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </button>
        </div>

        <div class="page-beta-editor-header__center">
            <beta-editor-viewport-toggle
                v-if="betaStore.isSitePreviewActive"
                :model-value="betaStore.previewDevice"
                @update:model-value="betaStore.setPreviewDevice"
            />

            <beta-editor-page-chip v-else :title="page?.title" :loading="loadingGetPage" />
        </div>

        <div class="page-beta-editor-header__right">
            <button
                type="button"
                :aria-pressed="betaStore.isSitePreviewActive"
                :class="clsx('button-preview', betaStore.isSitePreviewActive && 'is-active')"
                @click="betaStore.togglePreviewSurface"
            >
                <svg class="h-4 w-4" viewBox="0 0 20 20" fill="none">
                    <path d="M1.66699 10.0003C3.11177 7.16699 6.05588 5.20866 9.45866 5.20866C12.8614 5.20866 15.8055 7.16699 17.2503 10.0003C15.8055 12.8337 12.8614 14.792 9.45866 14.792C6.05588 14.792 3.11177 12.8337 1.66699 10.0003Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M9.45833 11.875C10.4939 11.875 11.3333 11.0355 11.3333 10C11.3333 8.96447 10.4939 8.125 9.45833 8.125C8.4228 8.125 7.58333 8.96447 7.58333 10C7.58333 11.0355 8.4228 11.875 9.45833 11.875Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <span>{{ betaStore.isSitePreviewActive ? 'Voltar ao editor' : 'Visualizar' }}</span>
            </button>

            <button
                type="button"
                @click="onClickPublish"
                class="button-publish"
                :disabled="loadingGetPage || editorPageStore.disabledPublish"
            >
                Salvar
            </button>
        </div>
    </div>
</template>

<style scoped>
.page-beta-editor-header {
    @apply fixed inset-x-0 top-0 z-50 grid h-[76px] grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4 border-b border-[#E3EAF4] bg-[rgba(245,247,251,0.92)] px-4 backdrop-blur-md md:px-6 lg:px-8;
}

.page-beta-editor-header--with-panel-navigation {
    left: 255px;
}

.page-beta-editor-header__left,
.page-beta-editor-header__right {
    @apply flex items-center gap-4;
}

.page-beta-editor-header__center {
    @apply flex justify-center;
}

.page-beta-editor-header__right {
    @apply justify-end;
}

.button-back {
    @apply flex h-11 w-11 items-center justify-center rounded-2xl border border-[#E4E8F1] bg-white text-[#44566F] shadow-[0_8px_24px_rgba(15,23,42,0.06)];
}

.button-preview {
    @apply inline-flex h-11 items-center gap-2 rounded-2xl border border-[#E4E8F1] bg-white px-4 text-sm font-semibold text-[#44566F] shadow-[0_8px_24px_rgba(15,23,42,0.06)];
}

.button-preview.is-active {
    @apply border-[#CFDAEB] bg-[#EEF4FF] text-[#173456];
}

.button-publish {
    @apply h-11 rounded-2xl px-6 text-sm font-semibold text-white shadow-md disabled:cursor-not-allowed disabled:bg-gray-500;
    background: var(--main-color);
}

@media (max-width: 1023px) {
    .page-beta-editor-header--with-panel-navigation {
        left: 0;
    }
}
</style>
